import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink, Search, ArrowLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { errMsg, formatDate } from "@/lib/api";
import { useAdminApi } from "@/admin/useAdminApi";
import { SCHEMAS } from "@/admin/schemas";
import FieldInput from "@/admin/Fields";
import { Loading } from "@/components/site/Ui";
import { reviewQuality, QUALITY_LABEL, QUALITY_CLS } from "@/admin/quality";

const QualityBadge = ({ q, testId }) => (
  <span className={`ml-2 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${QUALITY_CLS[q.status]}`} title={q.missing.join(", ")} data-testid={testId}>{QUALITY_LABEL[q.status]}</span>
);

const QualityPanel = ({ q }) => (
  <div className={`rounded-2xl border p-5 text-sm ${QUALITY_CLS[q.status]}`} data-testid="admin-quality-panel">
    <p className="font-semibold">Content quality: <span data-testid="admin-quality-status">{QUALITY_LABEL[q.status]}</span></p>
    <p className="mt-1 opacity-90">
      {q.status === "complete" ? "All required review sections are filled and sources are recorded. This page displays as a full review."
        : "Until this is Complete, the public page is shown only as a labelled product profile, not a full review."}
    </p>
    {q.missing.length > 0 && <ul className="mt-2 list-disc pl-5" data-testid="admin-quality-missing">{q.missing.map((m) => <li key={m}>{m}</li>)}</ul>}
    <p className="mt-2 text-xs opacity-80">Only published alternatives appear on the public page; a review needs at least one published alternative to display as a full review.</p>
  </div>
);

const STATUS_CLS = { published: "bg-[#ECF6F4] text-[#0F766E]", draft: "bg-[#E7EDEF] text-[#526174]", scheduled: "bg-amber-50 text-amber-700" };
export const StatusBadge = ({ s, at }) => s ? (
  <span className={`chip ${STATUS_CLS[s]}`}>{s === "scheduled" && at && new Date(at) <= new Date() ? "published (scheduled)" : s}</span>
) : null;

export function EntityList({ entity }) {
  const schema = SCHEMAS[entity];
  const call = useAdminApi();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [quality, setQuality] = useState("");
  const [sel, setSel] = useState([]);
  const bulk = async (to) => {
    if (!window.confirm(`Set ${sel.length} item(s) to "${to}"? Only publish content you have fact-checked.`)) return;
    let ok = 0;
    for (const sid of sel) {
      try { const d = (await call("get", `/admin/${entity}/${sid}`)).data; await call("put", `/admin/${entity}/${sid}`, { ...d, status: to }); ok++; } catch (e) { toast.error(errMsg(e)); }
    }
    toast.success(`${ok} updated`); setSel([]); load();
  };
  const load = () => call("get", `/admin/${entity}`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { setItems(null); load(); }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = useMemo(() => (items || []).filter((i) => i[schema.titleKey]?.toLowerCase().includes(q.toLowerCase()) && (!status || i.status === status) && (!quality || i.quality?.status === quality)), [items, q, status, quality, schema.titleKey]);
  const del = async (it) => {
    if (!window.confirm(`Delete "${it[schema.titleKey]}"? This cannot be undone.`)) return;
    try { await call("delete", `/admin/${entity}/${it.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`admin-list-${entity}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-3xl font-bold">{schema.label}</h1>
        <div className="flex gap-2">
          <div className="relative"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A96A8]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…" className="input !pl-9 !py-2" data-testid="admin-list-filter" /></div>
          {entity === "software" && items?.length > 0 && (
            <select value={quality} onChange={(e) => setQuality(e.target.value)} className="input !w-auto !py-2" data-testid="admin-list-quality-filter">
              <option value="">All quality states</option>
              {Object.entries(QUALITY_LABEL).map(([k, l]) => <option key={k} value={k}>{l} ({items.filter((i) => i.quality?.status === k).length})</option>)}
            </select>
          )}
          {items?.some((i) => i.status) && (
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="input !w-auto !py-2" data-testid="admin-list-status-filter">
              <option value="">All statuses ({items.length})</option>
              {["published", "draft", "scheduled"].map((s) => <option key={s} value={s}>{s} ({items.filter((i) => i.status === s).length})</option>)}
            </select>
          )}
          {!schema.noCreate && <Link to={`/admin/${entity}/new`} className="btn-primary" data-testid="admin-new-button"><Plus className="h-4 w-4" />New</Link>}
        </div>
      </div>
      {sel.length > 0 && (
        <div className="card px-5 py-3 mb-4 flex flex-wrap items-center gap-3" data-testid="admin-bulk-bar">
          <span className="text-sm font-medium text-[#17243A]">{sel.length} selected</span>
          <button className="btn-primary btn-sm" onClick={() => bulk("published")} data-testid="admin-bulk-publish">Publish</button>
          <button className="btn-secondary btn-sm" onClick={() => bulk("draft")} data-testid="admin-bulk-draft">Move to draft</button>
          <button className="btn-ghost btn-sm" onClick={() => setSel([])}>Clear</button>
        </div>
      )}
      {!items ? <Loading /> : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="bg-[#F1F4F6] text-left text-xs uppercase tracking-wider text-[#64728A]">
              {items.some((i) => i.status) && <th className="p-3 pl-5 w-8"><input type="checkbox" aria-label="Select all" checked={shown.length > 0 && sel.length === shown.length} onChange={(e) => setSel(e.target.checked ? shown.map((i) => i.id) : [])} className="h-4 w-4 accent-[#0F766E]" data-testid="admin-select-all" /></th>}
              <th className="p-3 pl-5">Title</th><th className="p-3">Status</th><th className="p-3">Updated</th><th className="p-3 pr-5 text-right sm:sticky right-0 bg-[#F1F4F6]">Actions</th></tr></thead>
            <tbody>
              {shown.map((it) => (
                <tr key={it.id} className="border-t border-[#E7EDEF] hover:bg-[#F1F4F6]" data-testid={`admin-row-${it.slug}`}>
                  {it.status !== undefined && <td className="p-3 pl-5"><input type="checkbox" aria-label={`Select ${it[schema.titleKey]}`} checked={sel.includes(it.id)} onChange={(e) => setSel(e.target.checked ? [...sel, it.id] : sel.filter((x) => x !== it.id))} className="h-4 w-4 accent-[#0F766E]" data-testid={`admin-select-${it.slug}`} /></td>}
                  <td className="p-3 pl-5">
                    <Link to={`/admin/${entity}/${it.id}`} className="font-semibold text-[#17243A] hover:text-[#3F5E7A]">{it[schema.titleKey]}</Link>
                    <div className="text-xs text-[#8A96A8]">/{it.slug}
                      {it.generated && <span className="ml-2 text-[#0F766E]">· AI-assisted draft, verify facts</span>}
                      {it.stub && <span className="ml-2 text-[#64728A]">· basic listing</span>}
                      {entity === "software" && it.quality && <QualityBadge q={it.quality} testId={`admin-quality-${it.slug}`} />}
                      {entity === "pages" && !it.owner_reviewed && <span className="ml-2 text-amber-600">· template, not reviewed</span>}</div>
                  </td>
                  <td className="p-3">{it.status ? <StatusBadge s={it.status} at={it.publish_at} /> : <span className="chip">always live</span>}
                    {it.status === "scheduled" && <div className="text-xs text-[#8A96A8] mt-1">{new Date(it.publish_at).toLocaleString()}</div>}</td>
                  <td className="p-3 text-[#64728A]">{formatDate(it.updated_at)}</td>
                  <td className="p-3 pr-5 sm:sticky right-0 bg-white/95">
                    <div className="flex justify-end gap-1">
                      <a href={`${schema.publicPath}${it.slug}`} target="_blank" rel="noreferrer" className="btn-ghost !p-2" aria-label="View" data-testid={`admin-view-${it.slug}`}><ExternalLink className="h-4 w-4" /></a>
                      <Link to={`/admin/${entity}/${it.id}`} className="btn-ghost !p-2" aria-label="Edit" data-testid={`admin-edit-${it.slug}`}><Pencil className="h-4 w-4" /></Link>
                      {!schema.noDelete && <button onClick={() => del(it)} className="btn-ghost !p-2 text-rose-600" aria-label="Delete" data-testid={`admin-delete-${it.slug}`}><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {!shown.length && <tr><td colSpan={4} className="p-10 text-center text-[#8A96A8]">No items</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const cleanLines = (a) => (a || []).map((x) => x.trim()).filter(Boolean);

function clean(entity, v) {
  const out = { ...v };
  SCHEMAS[entity].sections.flatMap((s) => s.fields).forEach((f) => {
    if (f.type === "lines") out[f.k] = cleanLines(out[f.k]);
    if (f.type === "faqs") out[f.k] = (out[f.k] || []).filter((x) => x.q.trim());
    if (f.type === "feature-rows") out[f.k] = (out[f.k] || []).filter((x) => x.feature.trim());
    if (f.type === "compare-items") out[f.k] = (out[f.k] || []).filter((i) => (v.software || []).includes(i.slug)).map((i) => ({ ...i, strengths: cleanLines(i.strengths), weaknesses: cleanLines(i.weaknesses) }));
  });
  return out;
}

export function EntityEditor({ entity }) {
  const schema = SCHEMAS[entity];
  const { id } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const call = useAdminApi();
  const [values, setValues] = useState(isNew ? schema.defaults : null);
  const initial = useRef(null);
  if (values && initial.current === null) initial.current = JSON.stringify(values);
  const [ctx, setCtx] = useState({ software: [], categories: [], authors: [], comparisons: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let off = false;
    if (!isNew) call("get", `/admin/${entity}/${id}`).then((r) => !off && setValues((v) => (v && v.id === r.data.id ? v : r.data)))
      .catch((e) => { if (!off) { toast.error(errMsg(e)); nav(`/admin/${entity}`); } });
    Promise.all(["software", "categories", "authors", "comparisons"].map((e) => call("get", `/admin/${e}`)))
      .then(([s, c, a, cp]) => !off && setCtx({ software: s.data, categories: c.data, authors: a.data, comparisons: cp.data })).catch(() => {});
    return () => { off = true; };
  }, [entity, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (p) => setValues((v) => ({ ...v, ...p }));
  const save = async (e, force = false) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const body = clean(entity, values);
      const r = isNew ? await call("post", `/admin/${entity}${force ? "?force=true" : ""}`, body) : await call("put", `/admin/${entity}/${id}`, body);
      toast.success(isNew ? "Created" : "Saved");
      initial.current = null;
      setValues(r.data);
      if (isNew) nav(`/admin/${entity}/${r.data.id}`, { replace: true });
    } catch (err) {
      const m = errMsg(err);
      if (m.startsWith("Possible duplicate") && window.confirm(`${m}\n\nCreate anyway?`)) { setBusy(false); return save(null, true); }
      toast.error(m);
    }
    setBusy(false);
  };
  if (!values) return <Loading />;
  const liveQ = entity === "software" ? reviewQuality({ ...values, alternatives: (values.alternatives || []).filter((a) => values.status !== "published" || ctx.software.some((s) => s.slug === a && s.status === "published")) }) : null;
  return (
    <form onSubmit={save} data-testid={`admin-editor-${entity}`} className="space-y-6 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={`/admin/${entity}`} className="btn-ghost !p-2" aria-label="Back" data-testid="admin-editor-back"><ArrowLeft className="h-4 w-4" /></Link>
          <h1 className="text-2xl font-bold">{isNew ? `New ${schema.singular}` : values[schema.titleKey]}</h1>
          <StatusBadge s={values.status} at={values.publish_at} />
        </div>
        {!isNew && <a href={`${schema.publicPath}${values.slug}`} target="_blank" rel="noreferrer" className="btn-secondary btn-sm" data-testid="admin-editor-view">View public page <ExternalLink className="h-3.5 w-3.5" /></a>}
      </div>
      {entity === "software" && <QualityPanel q={liveQ} />}
      {schema.sections.map((sec) => (
        <section key={sec.title} className="card p-6 space-y-5">
          <h2 className="font-display text-lg font-semibold text-[#17243A] pb-3 border-b border-[#EEF2F4]">{sec.title}</h2>
          {sec.fields.map((f) => (
            <div key={f.k}>
              {f.type !== "checkbox" && f.type !== "publish" && <label className="label" htmlFor={`field-${f.k.replace(/_/g, "-")}`}>{f.label}{f.required && " *"}</label>}
              <FieldInput f={f} value={values[f.k]} set={(v) => patch({ [f.k]: v })} values={values} ctx={{ ...ctx, patch }} />
              {f.hint && <p className="mt-1 text-xs text-[#64728A]">{f.hint}</p>}
            </div>
          ))}
        </section>
      ))}
      {!isNew && <Revisions entity={entity} id={id} onRestore={setValues} />}
      <div className="fixed bottom-0 right-0 left-0 lg:left-64 z-30 bg-white/95 backdrop-blur border-t border-[#DCE5E8] px-6 py-3 flex justify-end gap-2">
        {entity === "software" && <span className={`mr-auto inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold leading-none ${QUALITY_CLS[liveQ.status]}`} data-testid="admin-quality-chip">{QUALITY_LABEL[liveQ.status]}{liveQ.missing.length ? ` · ${liveQ.missing.length} missing` : ""}</span>}
        <Link to={`/admin/${entity}`} onClick={(e) => { if (JSON.stringify(values) !== initial.current && !window.confirm("Leave without saving? Unsaved changes will be lost.")) e.preventDefault(); }} className="btn-ghost" data-testid="admin-editor-cancel">Cancel</Link>
        <button disabled={busy} className="btn-primary" data-testid="admin-editor-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save</button>
      </div>
    </form>
  );
}

function Revisions({ entity, id, onRestore }) {
  const call = useAdminApi();
  const [revs, setRevs] = useState(null);
  const load = () => call("get", `/admin/${entity}/${id}/revisions`).then((r) => setRevs(r.data)).catch(() => setRevs([]));
  const restore = async (rev) => {
    if (!window.confirm(`Restore the version saved ${new Date(rev.saved_at).toLocaleString()}? The current version is kept in history.`)) return;
    try { const r = await call("post", `/admin/${entity}/${id}/revisions/${rev.id}/restore`); onRestore(r.data); toast.success("Version restored"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="card p-6" data-testid="admin-revisions">
      <div className="flex items-center justify-between"><h2 className="font-display text-lg font-semibold">Revision history</h2>
        {!revs && <button type="button" className="btn-secondary btn-sm" onClick={load} data-testid="admin-revisions-load">Show history</button>}</div>
      {revs && (revs.length ? (
        <ul className="mt-4 divide-y divide-[#EEF2F4] text-sm">{revs.map((r) => (
          <li key={r.id} className="py-2.5 flex items-center justify-between gap-3"><span>{new Date(r.saved_at).toLocaleString()} · {r.snapshot?.status || "saved"}</span>
            <button type="button" className="btn-ghost btn-sm" onClick={() => restore(r)} data-testid={`admin-revision-restore-${r.id}`}>Restore</button></li>))}</ul>
      ) : <p className="mt-3 text-sm text-[#64728A]">No earlier versions yet. A version is stored every time you save.</p>)}
    </section>
  );
}
